import {Injectable} from "@angular/core";
import {Actions, createEffect, ofType} from "@ngrx/effects";
import {Store} from "@ngrx/store";
import {combineLatest, map, of, switchMap} from "rxjs";
import {catchError, debounceTime, take, tap} from "rxjs/operators";
import {PlaybookListeningApiService} from "../playbook-listening.api.service";

import * as PlaybooksListingSelectors from "./playbooks.selectors";
import * as PlaybooksListingActions from './playbooks.actions';
import {TranslocoService} from "@jsverse/transloco";
import {GlobalService} from "app/global/global.service";
import {IPlaybooksItemDTO} from "../dto/playbooks-listening.dto";
import {NgbModal} from "@ng-bootstrap/ng-bootstrap";
import {
    AddPlaybookSideBarComponent
} from "../legal-hub-playbooks-listening/legal-hub-listing-side-bar/add-playbook-side-bar/add-playbook-side-bar.component";
import { EditPlaybookSideBarComponent } from "../legal-hub-playbooks-listening/legal-hub-listing-side-bar/edit-playbook-side-bar/edit-playbook-side-bar.component";


@Injectable({
    providedIn: 'root'
})
export class PlaybooksListingEffects {
    
    constructor(private _actions$: Actions, 
                private _playbooksListeningAPIService: PlaybookListeningApiService, 
                private _translocoService: TranslocoService,
                private _globalService: GlobalService,
                private _store:Store,
                private _modalService: NgbModal) {
    }

    fetchPlaybooks$ = createEffect(() => {
        return this._actions$
            .pipe(
                ofType(PlaybooksListingActions.fetchPlaybooks),
                debounceTime(1000),
                switchMap(() => {
                    return combineLatest({
                        search: this._store.select(PlaybooksListingSelectors.search),
                        filters:this._store.select(PlaybooksListingSelectors.defaultFilters),
                        paging: this._store.select(PlaybooksListingSelectors.paging)
                      
                    }).pipe(take(1))
                }),
                switchMap(({search, paging, filters}) => {
                     return this._playbooksListeningAPIService.getPlaybooks(paging?.currentPage ?? 1, search, filters)
                            .pipe(
                                map(result => PlaybooksListingActions.fetchPlaybooksSucceeded(result)),
                                catchError((error) => of(PlaybooksListingActions.fetchPlaybooksFailed(error)))
                            )
                    }
                )
            )
    });
    
    fetchCategories$ = createEffect(() => {
        return this._actions$
            .pipe(
                ofType(PlaybooksListingActions.fetchCategories),
                switchMap(({search}) => {
                    return this._playbooksListeningAPIService.getPlaybookCategories(search ||'')
                        .pipe(
                            map(result => PlaybooksListingActions.fetchCategoriesSucceeded(result)),
                            catchError((error) => of(PlaybooksListingActions.fetchCategoriesFailed(error)))
                        )
                }
            ))
    });

    filterByChanged$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(
                PlaybooksListingActions.searchChange,
                PlaybooksListingActions.defaultFilterChanged
            ),
            map(() => PlaybooksListingActions.fetchPlaybooks())
        )
    });

    pageChange$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(PlaybooksListingActions.changePage),
            map(() => PlaybooksListingActions.fetchPlaybooks()
            )
        );
    });

    addNewCategory$ = createEffect(() => {
        return this._actions$
            .pipe(
                ofType(PlaybooksListingActions.addNewCategory),
                switchMap(({categoryName}) => {
                        return this._playbooksListeningAPIService.addNewCategory(categoryName)
                            .pipe(
                                map(result => PlaybooksListingActions.addNewCategorySucceeded(result)),
                                catchError((error) => of(PlaybooksListingActions.addNewCategoryFailed(error)))
                            )
                    }
                ))
    });


    addNewPlaybook$ = createEffect(() => {
        return this._actions$
            .pipe(
                ofType(PlaybooksListingActions.addNewPlaybook),
                switchMap(({newPlaybook}) => {
                        return this._playbooksListeningAPIService.addPlaybook(newPlaybook)
                            .pipe(
                                map(result => PlaybooksListingActions.addNewPlaybookSucceeded(result)),
                                catchError((error) => of(PlaybooksListingActions.addNewPlaybookFailed(error)))
                            )
                    }
                ))
    });

     updatePlaybook$ = createEffect(() => {
        return this._actions$
            .pipe(
                ofType(PlaybooksListingActions.updatePlaybook),
                switchMap(({id, playbook}) => {
                        return this._playbooksListeningAPIService.updatePlaybook(id, playbook)
                            .pipe(
                                map(result => PlaybooksListingActions.updatePlaybookSucceeded(result)),
                                catchError((error) => of(PlaybooksListingActions.updatePlaybookSucceeded(error)))
                            )
                    }
                ))
    });   

    
     updateGuideline$ = createEffect(() => {
        return this._actions$
            .pipe(
                ofType(PlaybooksListingActions.updateGuideline),
                switchMap(({id, guideline}) => {
                        return this._playbooksListeningAPIService.updateGuideline(id, guideline)
                            .pipe(
                                map(result => PlaybooksListingActions.updateGuidelineSucceeded(result as unknown as IPlaybooksItemDTO[], id)),
                                catchError((error) => of(PlaybooksListingActions.updateGuidelineFailed(error)))
                            )
                    }
                ))
    });   


    apiResponseFailed$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(
                PlaybooksListingActions.addNewCategoryFailed,
                PlaybooksListingActions.addNewPlaybookFailed,

                PlaybooksListingActions.addNewPlaybookFailed,
                PlaybooksListingActions.updateGuidelineFailed
            ),
            tap(({error}) => {
                this._globalService.toastrError(error || this._translocoService.translate('Something went wrong message (general toast) - Something went wrong'));
            })
        )
    }, {
        dispatch: false
    });


    onEditPlaybookClicked$ = createEffect(() => this._actions$.pipe(
        ofType( PlaybooksListingActions.editPlaybookClicked),
        tap(({playbook}) => {
            const modalInstance = this._modalService.open(EditPlaybookSideBarComponent);
            modalInstance.componentInstance.playbookId = playbook?.id;
        })
    ), { dispatch: false });

    onAddNewPlaybookClicked$ = createEffect(() => this._actions$.pipe(
        ofType( PlaybooksListingActions.addNewPlaybookClicked),
        tap(() => {
            const modalInstance = this._modalService.open(AddPlaybookSideBarComponent);
            // we have to check the nullish coalescing operator here because the modal can be closed before the result is returned ex. on route change
            modalInstance?.result?.then((result: {refresh_listing: boolean}) => {
                if (result?.refresh_listing) {
                    this._store.dispatch(PlaybooksListingActions.fetchPlaybooks());
                }

            });
        })
    ), { dispatch: false });

    onPlaybookListingViewDestroyed$ = createEffect(() => this._actions$.pipe(
        ofType(PlaybooksListingActions.viewDestroyed),
        tap(() => {
            this._modalService.dismissAll();
        })
    ), { dispatch: false });




    deletePlayBook$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(PlaybooksListingActions.deletePlaybook),
            switchMap(({playbookId}) => {
                return this._playbooksListeningAPIService.deletePlayBook(playbookId).pipe(
                    map(() => {
                        this._modalService.dismissAll();
                        this._globalService.toastrSuccess( this._translocoService.translate('Delete playbook toaster message (Legal hub tab - section) - Playbook has been deleted successfully'));
                        return PlaybooksListingActions.deletePlaybookSuccess(playbookId);
                    }),
                    catchError(error => of(PlaybooksListingActions.deletePlaybookFailure(error)))
                )
            })
        )
    });

    deletePlaybookFailure$ = createEffect(() => this._actions$.pipe(
        ofType(PlaybooksListingActions.deletePlaybookFailure),
        tap(({error}) => {
            this._globalService.toastrError(error || this._translocoService.translate('Something went wrong message (general toast) - Something went wrong'));
        })
    ), { dispatch: false });
}